import { computed, ref } from 'vue';

/**
 * All available search types with their metadata
 */
const ALL_TYPES = [
    { key: 'courses', label: 'Courses', icon: 'book-alt' },
    { key: 'activities', label: 'Activities', icon: 'file' },
    { key: 'sections', label: 'Sections', icon: 'folder-open' },
    { key: 'announcements', label: 'Announcements', icon: 'megaphone' },
    { key: 'activity_updates', label: 'Activity Updates', icon: 'bell' },
    { key: 'discussions', label: 'Discussions', icon: 'comments' },
    { key: 'users', label: 'Users', icon: 'user' },
    { key: 'help_articles', label: 'Help Articles', icon: 'question-circle' },
];

const ALL_TYPE_KEYS = ALL_TYPES.map(t => t.key);

/**
 * Composable for managing search type filters
 *
 * Provides reactive state and methods for multi-select type filtering
 * in both GlobalSearch modal and SearchResults page.
 *
 * @param {Object} options - Configuration options
 * @param {Array<string>} options.initialTypes - Initial selected types (empty = show all)
 * @returns {Object} Type filter state and methods
 */
// eslint-disable-next-line import/prefer-default-export
export function useTypeFilters(options = {}) {
    const { initialTypes = [] } = options;

    const normalizeTypes = types => {
        if (!Array.isArray(types)) return [];

        const normalized = types
            .map(type => (typeof type === 'string' ? type.trim() : ''))
            .filter(Boolean)
            .filter(type => ALL_TYPE_KEYS.includes(type));

        return [...new Set(normalized)];
    };

    // State
    const selectedTypes = ref([...initialTypes]);
    const typeCounts = ref({});

    /**
     * Available types with counts attached
     */
    const availableTypes = computed(() =>
        ALL_TYPES.map(type => ({
            ...type,
            count: typeCounts.value[type.key] || 0,
        }))
    );

    /**
     * Check if any filters are active (not all selected, not none selected)
     */
    const hasActiveFilters = computed(
        () => selectedTypes.value.length > 0 && selectedTypes.value.length < ALL_TYPE_KEYS.length
    );

    /**
     * Check if all types are selected (or none, which means "show all")
     */
    const allSelected = computed(() => selectedTypes.value.length === ALL_TYPE_KEYS.length);

    /**
     * Check if no types are explicitly selected (means "show all" by default)
     */
    const noneSelected = computed(() => selectedTypes.value.length === 0);

    /**
     * Get the types to send to API (empty array = all types)
     */
    const typesForApi = computed(() => {
        // If none selected or all selected, send empty (backend defaults to all)
        if (noneSelected.value || allSelected.value) {
            return [];
        }
        return [...selectedTypes.value];
    });

    /**
     * Check if we're in multi-type mode (more than one type selected)
     */
    const isMultiTypeMode = computed(() => selectedTypes.value.length > 1);

    /**
     * Check if a specific type is selected
     * @param {string} typeKey - The type key to check
     * @returns {boolean}
     */
    const isSelected = typeKey => {
        // Only return true if explicitly selected
        // When nothing is selected (null state), no chips should appear active
        return selectedTypes.value.includes(typeKey);
    };

    /**
     * Toggle a type's selection state
     * @param {string} typeKey - The type key to toggle
     */
    const toggleType = typeKey => {
        if (!ALL_TYPE_KEYS.includes(typeKey)) return;

        const index = selectedTypes.value.indexOf(typeKey);
        if (index === -1) {
            selectedTypes.value.push(typeKey);
        } else {
            selectedTypes.value.splice(index, 1);
        }
    };

    /**
     * Select a specific type (add to selection)
     * @param {string} typeKey - The type key to select
     */
    const selectType = typeKey => {
        if (!ALL_TYPE_KEYS.includes(typeKey)) return;
        if (!selectedTypes.value.includes(typeKey)) {
            selectedTypes.value.push(typeKey);
        }
    };

    /**
     * Deselect a specific type (remove from selection)
     * @param {string} typeKey - The type key to deselect
     */
    const deselectType = typeKey => {
        const index = selectedTypes.value.indexOf(typeKey);
        if (index !== -1) {
            selectedTypes.value.splice(index, 1);
        }
    };

    /**
     * Set only one type as selected (like clicking a tab)
     * @param {string} typeKey - The type key to set as the only selected type
     */
    const setSingleType = typeKey => {
        if (!ALL_TYPE_KEYS.includes(typeKey)) return;
        selectedTypes.value = [typeKey];
    };

    /**
     * Clear all filters (show all types)
     */
    const clearAll = () => {
        selectedTypes.value = [];
    };

    /**
     * Select all types explicitly
     */
    const selectAll = () => {
        selectedTypes.value = [...ALL_TYPE_KEYS];
    };

    /**
     * Set selected types from an array
     * @param {Array<string>} types - Array of type keys to select
     */
    const setSelectedTypes = types => {
        selectedTypes.value = normalizeTypes(types);
    };

    /**
     * Update type counts from search response.
     * Merges with existing counts to preserve values for types not in current search.
     * @param {Object} counts - Object mapping type keys to counts
     * @param {boolean} replace - If true, replace all counts; if false (default), merge with existing
     */
    const setTypeCounts = (counts, replace = false) => {
        if (replace) {
            typeCounts.value = { ...counts };
        } else {
            typeCounts.value = { ...typeCounts.value, ...counts };
        }
    };

    /**
     * Get type label by key
     * @param {string} typeKey - The type key
     * @returns {string}
     */
    const getTypeLabel = typeKey => {
        const type = ALL_TYPES.find(t => t.key === typeKey);
        return type?.label || typeKey;
    };

    /**
     * Get type icon by key
     * @param {string} typeKey - The type key
     * @returns {string}
     */
    const getTypeIcon = typeKey => {
        const type = ALL_TYPES.find(t => t.key === typeKey);
        return type?.icon || 'search';
    };

    /**
     * Parse types from URL query string (comma-separated)
     * @param {string} typesParam - Comma-separated types string from URL
     */
    const parseFromUrl = typesParam => {
        if (!typesParam) {
            selectedTypes.value = [];
            return;
        }
        selectedTypes.value = normalizeTypes(typesParam.split(','));
    };

    /**
     * Convert selected types to URL-friendly string
     * @returns {string|null} Comma-separated types or null if none/all selected
     */
    const toUrlParam = () => {
        if (noneSelected.value || allSelected.value) {
            return null;
        }
        return selectedTypes.value.join(',');
    };

    return {
        // State
        selectedTypes,
        typeCounts,
        availableTypes,

        // Computed
        hasActiveFilters,
        allSelected,
        noneSelected,
        typesForApi,
        isMultiTypeMode,

        // Methods
        isSelected,
        toggleType,
        selectType,
        deselectType,
        setSingleType,
        clearAll,
        selectAll,
        setSelectedTypes,
        setTypeCounts,
        getTypeLabel,
        getTypeIcon,
        parseFromUrl,
        toUrlParam,
    };
}

/**
 * Export constants for use elsewhere
 */
export { ALL_TYPE_KEYS, ALL_TYPES };
